/**
 * Flush-on-background and the "Not saved yet" signal for the phone app.
 *
 * The console keeps document Y.Docs in memory only, and iOS suspends or kills
 * a backgrounded WebView process freely. So the app asks the console to flush
 * when it leaves the foreground (`flushPending`), and shows its own unsynced
 * indicator from `editState`. Both read the editor handles registered here by
 * the page routes; outside the app nothing registers and nothing posts.
 */

import type { CollabEditorFlushResult } from '@nimbalyst/collab-bundle/editor';
import { postToNative, type FlushPendingStatus } from './bridge';
import { isEmbedded } from './embedMarker';

export interface FlushableEditorHandle {
  flush(options?: { timeoutMs?: number }): Promise<CollabEditorFlushResult>;
  hasPendingWrites?(): boolean;
}

export interface FlushPendingResult {
  status: FlushPendingStatus;
  detail: string | null;
}

/** How often `editState.unsynced` is re-read while an editor is registered. */
const UNSYNCED_POLL_MS = 1_000;
/** Headroom over the editor's own timeout before this layer gives up on it. */
const FLUSH_GRACE_MS = 250;

/** How long a handle that left with unacknowledged edits gets to confirm them. */
const RETIRED_FLUSH_MS = 5_000;

const handles = new Set<FlushableEditorHandle>();
/**
 * Editors that unregistered with edits the server had not acknowledged. The
 * surface's teardown flush is still in flight when the handle is released, so
 * dropping it there reported "saved" while the edit could still be lost. Kept
 * until its flush acknowledges, or, after a failure, until the app discards it.
 */
interface RetiredEdit {
  settled: Promise<void>;
  failure: string | null;
}
const retired = new Map<FlushableEditorHandle, RetiredEdit>();
let editing = false;
let lastPosted: { editing: boolean; unsynced: boolean } | null = null;
let poll: ReturnType<typeof setInterval> | null = null;

function hasUnsyncedEdits(): boolean {
  if (retired.size > 0) return true;
  for (const handle of handles) {
    try {
      if (handle.hasPendingWrites?.()) return true;
    } catch {
      // A handle that cannot answer is not evidence of a pending write.
    }
  }
  return false;
}

/** Post `editState` when it changed since the last post (or always, with `force`). */
export function publishEditState(force = false): void {
  if (!isEmbedded()) return;
  const next = { editing, unsynced: hasUnsyncedEdits() };
  if (!force && lastPosted && lastPosted.editing === next.editing && lastPosted.unsynced === next.unsynced) return;
  lastPosted = next;
  postToNative({ type: 'editState', ...next });
}

function syncPoll(): void {
  if (handles.size > 0 && !poll) poll = setInterval(() => publishEditState(), UNSYNCED_POLL_MS);
  if (handles.size === 0 && poll) {
    clearInterval(poll);
    poll = null;
  }
}

/** Register a live editor handle; returns the unregister. No-op outside the app. */
export function registerEmbeddedEditorHandle(handle: FlushableEditorHandle): () => void {
  if (!isEmbedded()) return () => {};
  handles.add(handle);
  syncPoll();
  publishEditState();
  return () => {
    if (!handles.delete(handle)) return;
    if (hasPendingWrites(handle)) retire(handle);
    syncPoll();
    publishEditState();
  };
}

function hasPendingWrites(handle: FlushableEditorHandle): boolean {
  try {
    return handle.hasPendingWrites?.() === true;
  } catch {
    return false;
  }
}

function retire(handle: FlushableEditorHandle): void {
  const entry: RetiredEdit = { failure: null, settled: Promise.resolve() };
  entry.settled = settleWithin(handle.flush({ timeoutMs: RETIRED_FLUSH_MS }), RETIRED_FLUSH_MS + FLUSH_GRACE_MS)
    .then((result) => {
      if (result !== 'timed-out' && (result.status === 'acknowledged' || result.status === 'not-required')) {
        retired.delete(handle);
      } else {
        entry.failure = result === 'timed-out' ? 'timed-out' : failureDetail(result);
        console.warn('[embedded] An edit left on a closed page was not confirmed saved', entry.failure);
      }
      publishEditState();
    });
  retired.set(handle, entry);
}

/**
 * Forget edits that failed to save after their page closed. The app calls this
 * once the reader has chosen to leave without saving; returns how many it dropped.
 */
export function discardUnsyncedEdits(): number {
  let dropped = 0;
  for (const [handle, entry] of retired) {
    if (entry.failure === null) continue;
    retired.delete(handle);
    dropped += 1;
  }
  publishEditState();
  return dropped;
}

export function setEmbeddedEditing(next: boolean): void {
  if (editing === next) return;
  editing = next;
  publishEditState();
}

function settleWithin<T>(promise: Promise<T>, timeoutMs: number): Promise<T | 'timed-out'> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve('timed-out'), timeoutMs);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (cause: unknown) => {
        clearTimeout(timer);
        resolve({ status: 'failed', message: cause instanceof Error ? cause.message : 'The flush threw.' } as T);
      },
    );
  });
}

function failureDetail(result: CollabEditorFlushResult): string {
  switch (result.status) {
    case 'rejected': return 'rejected';
    case 'unavailable': return `unavailable:${result.reason}`;
    case 'failed': return result.message;
    default: return result.status;
  }
}

/**
 * Flush every registered editor and report one outcome: `failed` beats
 * `timed-out` beats `acknowledged` beats `not-required`. Never throws.
 */
export async function flushPending(timeoutMs: number): Promise<FlushPendingResult> {
  const budget = Number.isFinite(timeoutMs) && timeoutMs > 0 ? Math.min(timeoutMs, 30_000) : 5_000;
  if (handles.size === 0 && retired.size === 0) return { status: 'not-required', detail: null };
  const awaitedRetired = retired.size > 0;
  const [results] = await Promise.all([
    Promise.all([...handles].map((handle) => (
      settleWithin(handle.flush({ timeoutMs: budget }), budget + FLUSH_GRACE_MS)
    ))),
    settleWithin(Promise.all([...retired.values()].map((entry) => entry.settled)), budget + FLUSH_GRACE_MS),
  ]);
  publishEditState();
  // One verdict over both groups: failed > timed-out > acknowledged > not-required.
  const retiredFailure = [...retired.values()].find((entry) => entry.failure !== null);
  const liveFailure = results.find((r): r is CollabEditorFlushResult => (
    r !== 'timed-out' && r.status !== 'acknowledged' && r.status !== 'not-required' && r.status !== 'timed-out'
  ));
  if (retiredFailure) return { status: 'failed', detail: retiredFailure.failure };
  if (liveFailure) return { status: 'failed', detail: failureDetail(liveFailure) };
  if (retired.size > 0 || results.some((r) => r === 'timed-out' || r.status === 'timed-out')) {
    return { status: 'timed-out', detail: null };
  }
  if (awaitedRetired || results.some((r) => r !== 'timed-out' && r.status === 'acknowledged')) {
    return { status: 'acknowledged', detail: null };
  }
  return { status: 'not-required', detail: null };
}

/** Tests only. */
export function resetPendingEditsForTests(): void {
  handles.clear();
  retired.clear();
  editing = false;
  lastPosted = null;
  if (poll) clearInterval(poll);
  poll = null;
}
